import React, { useState, useEffect } from "react";
import { StyleSheet, View, TouchableOpacity, Platform } from "react-native";
import {
  MediaModel,
  ShareModel,
  Contact,
  ContactModel,
  GroupModel,
  IMediaMetadata,
  resolveMediaUrls,
  isAudioMedia,
  isVideoMedia,
  isImageMedia,
  isPdfMedia,
  isDocumentMedia,
  getMediaType,
  formatLocation,
  DateParser,
} from "common247";
import { ThemedText } from "../ThemedText";
import { ThemedButton } from "../ThemedButton";
import { ThemedRating } from "../ThemedRating";
import { ThemedProgressBar } from "../ThemedProgressBar";
import { ShareSelector } from "../sharing/ShareSelector";
import { useThemeColor } from "../../hooks/useThemeColor";
import { ButtonPaddings } from "@/constants/themes/components";
import { Ionicons } from "@expo/vector-icons";
import { extractMediaMetadata, hasExtractedMetadata } from "@/services/media-metadata";
import { useFeatureFlag } from "@/services/remote-config-service";
import { ExtractMediaInfoModal } from "./ExtractMediaInfoModal";
import { ThemedKebabMenu, type IKebabActionItem } from "../ThemedKebabMenu";
import { VideoPlayer } from "./VideoPlayer";
import { downloadMediaFile } from "@/utils/media-download";
import { useFirebase } from "../firebase/useFirebase";
import { editMedia } from "@/services/firebase/media-functions";
import { BrandColors } from "@/constants/ThemeColours";
import { LazyMediaImage } from "./LazyMediaImage";
import { ThemedPlatformSymbol } from "../ThemedPlatformSymbol";
import { ThemedChip } from "../ThemedChip";
import { ThemedCompactCard } from "../cards/ThemedCompactCard";
import { ThemedMediumCard } from "../cards/ThemedMediumCard";
import { ThemedFullCard } from "../cards/ThemedFullCard";
import { ThemedCustomCard } from "../cards/ThemedCustomCard";
import {
  resolveCardLinkedContacts,
  resolveCardLinkedGroups,
  resolveCardLinkedMemories,
  resolveCardLinkedPlaces,
  resolveCardLinkedAnchors,
} from "../cards/card-helpers";
import TagListStripDefault, { TagListStrip as TagListStripNamed } from "@/components/tags/TagListStrip";
const TagListStrip = TagListStripNamed || TagListStripDefault;


import { isDateUserDefined, ICustomCardEntityConfig } from "common247";
import {
  formatFileSize,
  formatContentType,
  formatDuration,
  formatDimensions,
  formatCameraSettings,
  formatShortHash,
} from "./media-formatters";
export {
  formatFileSize,
  formatContentType,
  formatDuration,
  formatDimensions,
  formatCameraSettings,
  formatShortHash,
};

/**
 * Displays offline-extracted media metadata: captured date, GPS location, camera settings, dimensions, objects found, and description.
 */
import {
  MediaMetadataSection,
  MediaStatsSection,
  MediaHoverMetadataFooter,
  formatUploadedDateTime,
  type IMediaStatsSectionProps,
  type IMediaHoverMetadataFooterProps,
} from "./MediaCardSections";
import { MediaPreview, type IMediaPreviewProps } from "./MediaPreview";

export {
  MediaMetadataSection,
  MediaStatsSection,
  MediaHoverMetadataFooter,
  formatUploadedDateTime,
  MediaPreview,
  resolveMediaUrls,
  isAudioMedia,
  isVideoMedia,
  isPdfMedia,
  isDocumentMedia,
  type IMediaStatsSectionProps,
  type IMediaHoverMetadataFooterProps,
  type IMediaPreviewProps,
};

/**
 * Props for the MediaCard component.
 */
export interface IMediaCardProps {
  media: MediaModel;
  onPress?: (event?: any) => void;
  onLongPress?: () => void;
  onContextMenu?: (e?: any) => void;
  selectionMode?: boolean;
  isSelected?: boolean;
  showDetails?: boolean;
  canEdit?: boolean;
  onEditPress?: () => void;
  onShare?: () => void;
  onNativeShare?: () => void;
  /** Optional callback triggered to download the media item (defaults to standard browser download) */
  onDownload?: () => void;
  onDelete?: () => void;
  showOwner?: boolean;
  /** Whether the card is rendered in an administrative management screen */
  isAdminView?: boolean;
  /** Whether the user is allowed to add or remove tags (explicit override) */
  canEditTags?: boolean;
  /** Current authenticated user ID for tag removal authorization */
  currentUserId?: string;

  /** Existing shares associated with this media */
  shares?: ShareModel[];
  /** Available contacts to select from when adding a new share */
  availableContacts?: (Contact | ContactModel)[];
  /** Available groups to select from when adding a new share */
  availableGroups?: GroupModel[];
  /** Available places to resolve linked place metadata */
  availablePlaces?: any[];
  /** Available anchors to resolve linked anchor metadata */
  availableAnchors?: any[];
  /** Loading flag while fetching shares */
  isLoadingShares?: boolean;
  /** Callback when a new share is created or role updated */
  onShareTarget?: (targetId: string, targetType: "contact" | "group", role: "viewer" | "editor") => void;
  /** Callback when a share is revoked */
  onUnshare?: (shareId: string) => void;
  /** Controlled expanded state for sharing control */
  isSharingExpanded?: boolean;
  /** Callback to toggle expanded sharing control */
  onToggleSharing?: () => void;
  /** Whether to render in compact density mode */
  compact?: boolean;
  /** Presentation density and thumbnail size mode (default: "medium") */
  viewMode?: MediaViewMode;
  /** Whether the card is currently hovered */
  isHovered?: boolean;
  /** Optional callback when the favourite rating is changed (1-5) */
  onRateChange?: (newRating: number) => void;
  /** Optional custom card configuration */
  config?: ICustomCardEntityConfig;
}

/**
 * Display presentation density and thumbnail size mode for media items.
 */
export type MediaViewMode = "details" | "compact" | "small" | "medium" | "large" | "chip" | "full" | "custom";

export function MediaCard({
  media,
  onPress,
  onLongPress,
  onContextMenu,
  selectionMode = false,
  isSelected = false,
  showDetails = false,
  canEdit = false,
  onEditPress,
  onShare,
  onNativeShare,
  onDownload,
  onDelete,
  showOwner = false,
  isAdminView = false,
  canEditTags,
  currentUserId,
  shares,
  availableContacts,
  availableGroups,
  availablePlaces,
  availableAnchors,
  isLoadingShares,
  onShareTarget,
  onUnshare,
  isSharingExpanded,
  onToggleSharing,
  compact = false,
  viewMode = "medium",
  isHovered = false,
  onRateChange,
  config,
}: IMediaCardProps) {
  const services = useFirebase();
  const [localExpanded, setLocalExpanded] = useState(false);
  const isExpanded = isSharingExpanded !== undefined ? isSharingExpanded : localExpanded;

  const [rating, setRating] = useState<number>(media.favouriteRating ?? 3);

  useEffect(() => {
    setRating(media.favouriteRating ?? 3);
  }, [media.favouriteRating]);

  const [lastKnownShares, setLastKnownShares] = useState<ShareModel[] | null>(null);

  useEffect(() => {
    if (shares !== undefined) {
      setLastKnownShares(shares);
    }
  }, [shares]);

  const [isCardHovered, setIsCardHovered] = useState(false);
  const activeHovered = isHovered || isCardHovered;

  const userRole = isAdminView ? "viewer" : (media.userRole || "owner");
  const isOwner = isAdminView ? false : (userRole === "owner");
  const isViewer = isAdminView ? true : (userRole === "viewer");
  const roleName = isOwner ? undefined : isViewer ? "Shared Media (View Only)" : "Shared Media";
  const canShare = !isAdminView && (media.canShare ?? isOwner);
  const sharedTo = media.sharedTo || [];
  const memoryCount = media.memoryItems?.length ?? media.memoryIds?.length ?? 0;
  const dimensions = formatDimensions(media.metadata?.width, media.metadata?.height);
  const resolvedShowOwner = showOwner || !isOwner;

  const resolvedContacts = resolveCardLinkedContacts(media, availableContacts);
  const resolvedGroups = resolveCardLinkedGroups(media, availableGroups);
  const resolvedMemories = resolveCardLinkedMemories(media);
  const resolvedPlaces = resolveCardLinkedPlaces(media, availablePlaces);
  const resolvedAnchors = resolveCardLinkedAnchors(media, availableAnchors);

  const cardBgColor = useThemeColor({}, "card");
  const borderColor = useThemeColor({}, "border");
  const hoverBorderColor = useThemeColor({}, "buttonHoverBorder");
  const badgeBg = useThemeColor({}, "buttonSecondary");
  const tintColor = useThemeColor({}, "tint");
  const textMuted = useThemeColor({}, "textMuted");
  const activeBorderColor = isSelected ? tintColor : activeHovered ? hoverBorderColor : borderColor;

  const hoverHandlers = Platform.OS === "web" ? {
    onMouseEnter: () => setIsCardHovered(true),
    onMouseLeave: () => setIsCardHovered(false),
  } : {};

  const contextMenuHandler = Platform.OS === "web" && onContextMenu ? {
    onContextMenu: (e: any) => {
      e.preventDefault();
      onContextMenu(e);
    },
  } : {};

  const { primaryUrl, fallbackUrl } = resolveMediaUrls(media);
  const effectiveCanEdit = !isAdminView && (canEdit || isOwner);
  const effectiveCanEditTags = canEditTags !== undefined ? canEditTags : (!isAdminView && effectiveCanEdit);
  const effectiveIsOwnerForTags = (canEditTags === false || isAdminView) ? false : isOwner;

  const handleRatingChange = async (newRating: number) => {
    setRating(newRating);
    if (onRateChange) {
      onRateChange(newRating);
    }
    if (services && media.id && effectiveCanEdit) {
      try {
        await editMedia(services, {
          mediaId: media.id,
          favouriteRating: newRating,
        });
      } catch (err) {
        console.warn("Failed to update media favourite rating:", err);
      }
    }
  };

  const handleSharePress = (e?: any) => {
    if (e?.stopPropagation) e.stopPropagation();
    if (onToggleSharing) {
      onToggleSharing();
    } else if (onShareTarget || shares !== undefined) {
      setLocalExpanded(!localExpanded);
    } else if (onShare) {
      onShare();
    } else {
      setLocalExpanded(!localExpanded);
    }
  };

  const effectiveShares: ShareModel[] =
    shares ??
    lastKnownShares ??
    (media.sharedWith?.map((s) => ({
      id: s.id,
      sourceId: media.id || "",
      sourceType: "media" as const,
      targetId: s.id,
      targetType: s.type,
      role: s.role,
      targetDisplayName: s.displayName,
      targetUserId: s.targetUserId,
      sharedBy: media.ownerId || "",
      sharedAt: media.createdAt,
    })) as ShareModel[]) ??
    [];

  const canShowShareButton = canShare && (onShare || onShareTarget || onToggleSharing || shares !== undefined);
  const mediaTitle = media.title || "Media";
  const effectiveDate = media.date;

  const handleDownload = () => {
    if (onDownload) {
      onDownload();
    } else if (media.downloadUrl) {
      downloadMediaFile(media.downloadUrl, mediaTitle);
    }
  };

  const isExtractEnabled = useFeatureFlag("enable_extract_media_info", true);
  const [isExtracting, setIsExtracting] = useState(false);
  const [extractedMetadata, setExtractedMetadata] = useState<IMediaMetadata | null>(null);
  const [extractModalVisible, setExtractModalVisible] = useState(false);

  const handleExtractMediaInfo = async () => {
    if (isExtracting) return;
    setIsExtracting(true);
    try {
      const targetPayload = media;
      const res = await extractMediaMetadata(targetPayload, media.contentType || "image/jpeg");
      setExtractedMetadata(res);
      setExtractModalVisible(true);
    } catch (err) {
      console.warn("[MediaCard] Metadata extraction failed:", err);
    } finally {
      setIsExtracting(false);
    }
  };

  const handleApplyExtractedInfo = async (applied: any) => {
    if (services && media.id) {
      try {
        await editMedia(services, {
          mediaId: media.id,
          ...applied,
        });
      } catch (err) {
        console.error("Failed to update media with extracted info:", err);
      }
    }
  };

  const renderExtractModal = () => (
    <ExtractMediaInfoModal
      visible={extractModalVisible}
      media={media}
      extractedMetadata={extractedMetadata}
      onApply={handleApplyExtractedInfo}
      onClose={() => setExtractModalVisible(false)}
    />
  );

  const kebabActions: IKebabActionItem[] = [];
  if (onNativeShare) {
    kebabActions.push({
      id: "nativeShare",
      label: "Send To",
      icon: "paper-plane-outline",
      onPress: onNativeShare,
    });
  }
  if (isExtractEnabled && effectiveCanEdit) {
    kebabActions.push({
      id: "extractMediaInfo",
      label: isExtracting ? "Extracting Media Info..." : "Extract Media Info",
      icon: "sparkles-outline",
      onPress: handleExtractMediaInfo,
      disabled: isExtracting,
      testID: `media-extract-info-${media.id || "btn"}`,
    });
  }
  if (media.downloadUrl || onDownload) {
    kebabActions.push({
      id: "download",
      label: "Download",
      icon: "download-outline",
      onPress: handleDownload,
      testID: `media-download-${media.id || "btn"}`,
    });
  }
  if (canShowShareButton) {
    kebabActions.push({
      id: "share",
      label: isExpanded ? "Close Sharing" : "Manage Sharing",
      icon: "share-social-outline",
      onPress: handleSharePress,
    });
  }
  if (canEdit && onEditPress) {
    kebabActions.push({
      id: "edit",
      label: "Edit Media",
      icon: "pencil-outline",
      onPress: onEditPress,
    });
  }
  if (isOwner && onDelete) {
    kebabActions.push({
      id: "delete",
      label: "Delete Media",
      icon: "trash-outline",
      destructive: true,
      onPress: onDelete,
    });
  }

  if (showDetails) {
    return (
      <View style={styles.detailContainer}>
        <ThemedProgressBar loading={isExtracting} />
        <View style={styles.detailPreviewWrapper}>
          <MediaPreview
            url={media.downloadUrl}
            contentType={media.contentType}
            name={mediaTitle}
            style={styles.fullImage}
            resizeMode="contain"
            allowInlinePlayer={true}
            isUnavailable={media.isUnavailable}
          />
          {kebabActions.length > 0 && (
            <View style={styles.detailTopRightKebab}>
              <ThemedKebabMenu
                actions={kebabActions}
                compact={compact}
                accessibilityLabel={`Options for ${mediaTitle}`}
                triggerStyle={styles.mediaKebabTrigger}
              />
            </View>
          )}
        </View>
        <View style={styles.infoSection}>
          <View style={styles.titleRow}>
            <ThemedText type="editorialTitle" style={styles.fileName}>
              {mediaTitle}
            </ThemedText>
            {isExtractEnabled && effectiveCanEdit && (
              <ThemedButton
                title={isExtracting ? "Extracting..." : "Extract Media Info"}
                icon={isExtracting ? undefined : "sparkles-outline"}
                variant="minimal"
                size="small"
                onPress={handleExtractMediaInfo}
                disabled={isExtracting}
                loading={isExtracting}
                testID={`media-extract-btn-${media.id || "card"}`}
              />
            )}
          </View>

          {media.isUnavailable ? (
            <View style={styles.unavailableWarningBadge} testID="media-unavailable-badge">
              <Ionicons name="alert-circle" size={13} color="#DC3545" />
              <ThemedText style={styles.unavailableWarningText}>External Media Unavailable</ThemedText>
            </View>
          ) : null}

          <View style={styles.detailRatingRow}>
            <ThemedRating
              value={rating}
              onChange={handleRatingChange}
              size={22}
              showLabel={true}
              label="Favourite:"
              readOnly={!effectiveCanEdit}
            />
          </View>

          {formatLocation(media.location) ? <ThemedText style={styles.locationText}>📍 {formatLocation(media.location)}</ThemedText> : null}

          {media.description ? <ThemedText style={styles.descriptionText}>{media.description}</ThemedText> : null}

          <MediaStatsSection
            media={media}
            badgeBg={badgeBg}
            borderColor={borderColor}
            tintColor={tintColor}
          />

          {media.metadata || (media.mediaTarget && media.mediaTarget !== "Unknown") ? (
            <MediaMetadataSection metadata={media.metadata || {}} mediaTarget={media.mediaTarget} badgeBg={badgeBg} tintColor={tintColor} />
          ) : null}

          {/* Compact Sharing Indicator Pill in details view */}
          {effectiveShares.length > 0 && (
            <View style={styles.sharePillContainer}>
              <TouchableOpacity
                style={[styles.compactSharePill, { backgroundColor: badgeBg, borderColor }]}
                onPress={canShare ? handleSharePress : undefined}
                activeOpacity={canShare ? 0.7 : 1}
                accessibilityLabel={`Shared with ${effectiveShares.length} recipients. Tap to manage sharing.`}
                testID={`media-share-pill-details-${media.id || "card"}`}
              >
                <Ionicons name="people-outline" size={12} color={tintColor} style={{ marginRight: 4 }} />
                <ThemedText style={[styles.compactShareText, { color: tintColor }]}>
                  Shared ({effectiveShares.length})
                </ThemedText>
              </TouchableOpacity>
            </View>
          )}

          <TagListStrip
            entityId={media.id!}
            entityType="media"
            tags={media.tags}
            canEdit={effectiveCanEditTags}
            isOwner={effectiveIsOwnerForTags}
            currentUserId={currentUserId}
          />

          {effectiveDate ? (
            <View style={styles.detailDateRow} testID={`media-date-detail-${media.id || "card"}`}>
              <Ionicons name={isDateUserDefined(effectiveDate) ? "calendar" : "calendar-outline"} size={13} color={tintColor} />
              <ThemedText style={[styles.metaText, { color: tintColor, fontWeight: "600" }]}>
                Date: {DateParser.parse(effectiveDate).formatted} {isDateUserDefined(effectiveDate) ? "(Manual)" : "(Captured)"}
              </ThemedText>
            </View>
          ) : null}

          <View style={styles.detailUploadedRow}>
            <Ionicons name="time-outline" size={13} color={tintColor} style={{ opacity: 0.7 }} />
            <ThemedText style={styles.metaText}>
              Uploaded {formatUploadedDateTime(media.createdAt)}
            </ThemedText>
          </View>

          <View style={styles.badgeRow}>
            {isOwner ? (
              <ThemedPlatformSymbol type="owner" size="small" />
            ) : (
              <ThemedPlatformSymbol type="shared" size="small" />
            )}
            {!isOwner && roleName ? (
              <View style={[styles.roleBadge, { backgroundColor: badgeBg }]}>
                <ThemedText type="stamp" style={styles.roleBadgeText}>{roleName}</ThemedText>
              </View>
            ) : null}
            {!isOwner && <ThemedText style={[styles.ownerText, { color: tintColor }]}>• Shared by: {media.ownerDisplayName || "Unknown"}</ThemedText>}
          </View>
        </View>

        <ShareSelector
          visible={isExpanded}
          onClose={handleSharePress}
          itemTitle={mediaTitle}
          itemType="media"
          shares={effectiveShares}
          contacts={availableContacts || []}
          groups={availableGroups || []}
          isLoading={isLoadingShares}
          canShare={canShare}
          canUnshare={canShare}
          onShare={onShareTarget || (() => {})}
          onUnshare={onUnshare || (() => {})}
          testID={`media-share-picker-details-${media.id || "card"}`}
        />
        {renderExtractModal()}
      </View>
    );
  }

  if (viewMode === "chip") {
    return (
      <View style={{ margin: 2 }}>
        <ThemedChip
          entityType="media"
          id={media.id}
          label={mediaTitle}
          thumbnailUrl={media.thumbnailUrl || media.url}
          isOwner={isOwner}
          isFiveStar={rating === 5}
          onPress={onPress}
          onLongPress={onLongPress}
        />
      </View>
    );
  }

  if (viewMode === "medium") {
    const formattedDate = effectiveDate
      ? `${DateParser.parse(effectiveDate).formatted} ${isDateUserDefined(effectiveDate) ? "(Manual)" : "(Captured)"}`
      : formatUploadedDateTime(media.createdAt);
    const heroPreviewUrl = primaryUrl || fallbackUrl || media.url || media.thumbnailUrl || media.downloadUrl;

    const linkedContacts = resolvedContacts;
    const linkedGroups = resolvedGroups;
    const linkedMemories =
      resolvedMemories.length > 0
        ? resolvedMemories
        : memoryCount > 0
          ? [{ id: (media as any).memoryId || "memory-link", title: `${memoryCount} memories` }]
          : [];
    const effectiveTags = Array.isArray(media.tags) ? media.tags : Object.values(media.tags || {});

    return (
      <View style={{ position: "relative" }}>
        <ThemedProgressBar loading={isExtracting} />
        <ThemedMediumCard
          entityType="media"
          id={media.id}
          title={mediaTitle}
          formattedDate={formattedDate}
          description={media.description}
          thumbnailUrl={heroPreviewUrl}
          ownerDisplayName={resolvedShowOwner ? media.ownerDisplayName : undefined}
          isOwner={isOwner}
          isFiveStar={rating === 5}
          kebabActions={kebabActions}
          contactsList={linkedContacts}
          groupsList={linkedGroups}
          memoriesList={linkedMemories}
          placesList={resolvedPlaces}
          anchorsList={resolvedAnchors}
          tagsList={effectiveTags}
          sharesList={effectiveShares}
          createdAt={media.createdAt}
          onPress={onPress}
          onEditPress={onEditPress}
          onDeletePress={onDelete}
          onSharePress={onShare || handleSharePress}
        />
        {renderExtractModal()}
      </View>
    );
  }

  if (viewMode === "full") {
    const formattedDate = effectiveDate
      ? `${DateParser.parse(effectiveDate).formatted} ${isDateUserDefined(effectiveDate) ? "(Manual)" : "(Captured)"}`
      : formatUploadedDateTime(media.createdAt);
    const heroPreviewUrl = primaryUrl || fallbackUrl || media.url || media.thumbnailUrl || media.downloadUrl;

    const linkedContacts = resolvedContacts;
    const linkedGroups = resolvedGroups;
    const linkedMemories =
      resolvedMemories.length > 0
        ? resolvedMemories
        : memoryCount > 0
          ? [{ id: (media as any).memoryId || "memory-link", title: `${memoryCount} memories` }]
          : [];
    const effectiveTags = Array.isArray(media.tags) ? media.tags : Object.values(media.tags || {});

    return (
      <View style={{ position: "relative" }}>
        <ThemedProgressBar loading={isExtracting} />
        <ThemedFullCard
          entityType="media"
          id={media.id}
          title={mediaTitle}
          formattedDate={formattedDate}
          description={media.description}
          thumbnailUrl={heroPreviewUrl}
          ownerDisplayName={resolvedShowOwner ? media.ownerDisplayName : undefined}
          isOwner={isOwner}
          isFiveStar={rating === 5}
          kebabActions={kebabActions}
          contactsList={linkedContacts}
          groupsList={linkedGroups}
          memoriesList={linkedMemories}
          placesList={resolvedPlaces}
          anchorsList={resolvedAnchors}
          tagsList={effectiveTags}
          sharesList={effectiveShares}
          createdAt={media.createdAt}
          onPress={onPress}
          onEditPress={onEditPress}
          onDeletePress={onDelete}
          onSharePress={onShare || handleSharePress}
        />
        {renderExtractModal()}
      </View>
    );
  }

  if (viewMode === "custom") {
    const effectiveTags = Array.isArray(media.tags) ? media.tags : Object.values(media.tags || {});
    return (
      <View style={{ position: "relative" }}>
        <ThemedProgressBar loading={isExtracting} />
        <ThemedCustomCard
          entityType="media"
          id={media.id}
          item={media}
          title={mediaTitle}
          description={media.description}
          thumbnailUrl={media.url || media.thumbnailUrl}
          isOwner={isOwner}
          isFiveStar={rating === 5}
          kebabActions={kebabActions}
          placesList={resolvedPlaces}
          tagsList={effectiveTags}
          sharesList={effectiveShares}
          config={config}
          onPress={onPress}
          onEditPress={onEditPress}
          onDeletePress={onDelete}
          onSharePress={onShare || handleSharePress}
        />
        {renderExtractModal()}
      </View>
    );
  }

  if (viewMode === "details") {
    return (
      <View
        style={[
          styles.detailsRowContainer,
          { backgroundColor: cardBgColor, borderColor: activeBorderColor },
        ]}
      >
        <ThemedProgressBar loading={isExtracting} />
        <TouchableOpacity
          style={styles.detailsRowMain}
          onPress={onPress}
          onLongPress={onLongPress}
          delayLongPress={350}
          activeOpacity={0.7}
          {...(Platform.OS === "web" && onContextMenu ? {
            onContextMenu: (e: any) => {
              e.preventDefault();
              onContextMenu(e);
            },
          } : {})}
        >
          {selectionMode || isSelected ? (
            <View style={[styles.detailsCheckbox, isSelected && { backgroundColor: tintColor, borderColor: tintColor }]}>
              {isSelected && <Ionicons name="checkmark" size={12} color="#FFFFFF" />}
            </View>
          ) : null}

          <View style={styles.detailsThumb}>
            <MediaPreview
              url={primaryUrl}
              fallbackUrl={fallbackUrl}
              contentType={media.contentType}
              name={mediaTitle}
              style={styles.detailsImage}
              resizeMode="cover"
              isUnavailable={media.isUnavailable}
            />
          </View>

          <View style={styles.detailsInfo}>
            <View style={styles.detailsNameRow}>
              <ThemedText type="defaultSemiBold" style={styles.detailsFileName} numberOfLines={1}>
                {mediaTitle}
              </ThemedText>
              {!isOwner && (
                <View style={[styles.inlineRoleBadge, { backgroundColor: badgeBg }]}>
                  <ThemedText style={styles.gridRoleBadgeText}>{userRole}</ThemedText>
                </View>
              )}
            </View>

            {media.isUnavailable ? (
              <View style={styles.unavailableWarningBadge} testID="media-unavailable-badge">
                <Ionicons name="alert-circle" size={11} color="#DC3545" />
                <ThemedText style={styles.unavailableWarningText}>External Media Unavailable</ThemedText>
              </View>
            ) : null}

            <View style={styles.detailsSubRow}>
              <View style={styles.detailsRatingWrapper} onStartShouldSetResponder={() => true}>
                <ThemedRating
                  value={rating}
                  onChange={handleRatingChange}
                  size={12}
                  showLabel={false}
                  readOnly={!effectiveCanEdit}
                />
              </View>
              {effectiveDate ? (
                <ThemedText style={[styles.detailsMetaText, { color: tintColor, fontWeight: "600" }]} numberOfLines={1}>
                  • 📅 {DateParser.parse(effectiveDate).formatted} {isDateUserDefined(effectiveDate) ? "(Manual)" : ""}
                </ThemedText>
              ) : null}
              <ThemedText style={styles.detailsMetaText} numberOfLines={1}>
                • {media.metadata?.capturedAt
                  ? `Captured: ${formatUploadedDateTime(media.metadata.capturedAt)}`
                  : `Uploaded: ${formatUploadedDateTime(media.createdAt)}`}
              </ThemedText>
              <ThemedText style={styles.detailsMetaText} numberOfLines={1}>
                • {formatFileSize(media.fileSizeBytes)}
              </ThemedText>
              <ThemedText style={styles.detailsMetaText} numberOfLines={1}>
                • {formatContentType(media.contentType)}
              </ThemedText>
              {dimensions ? (
                <ThemedText style={styles.detailsMetaText} numberOfLines={1}>
                  • {dimensions}
                </ThemedText>
              ) : null}
              {memoryCount > 0 ? (
                <View style={[styles.inlineMemoryBadge, { backgroundColor: badgeBg }]}>
                  <ThemedText style={[styles.inlineMemoryBadgeText, { color: tintColor }]}>
                    📖 {memoryCount}
                  </ThemedText>
                </View>
              ) : null}
              {formatLocation(media.location) ? (
                <ThemedText style={styles.detailsMetaText} numberOfLines={1}>
                  • 📍 {formatLocation(media.location)}
                </ThemedText>
              ) : null}
              {media.fileHash ? (
                <ThemedText style={styles.detailsMetaText} numberOfLines={1}>
                  • {formatShortHash(media.fileHash)}
                </ThemedText>
              ) : null}
            </View>
            <TagListStrip
              entityId={media.id!}
              entityType="media"
              tags={media.tags}
              canEdit={effectiveCanEditTags}
              isOwner={effectiveIsOwnerForTags}
              currentUserId={currentUserId}
              compact={true}
            />
            {effectiveShares.length > 0 && (
              <TouchableOpacity
                onPress={canShare ? handleSharePress : undefined}
                style={{ marginTop: 2, alignSelf: "flex-start" }}
                activeOpacity={canShare ? 0.7 : 1}
                testID={`media-share-pill-details-row-${media.id || "card"}`}
              >
                <ThemedText style={[styles.detailsMetaText, { color: tintColor, fontWeight: "600" }]} numberOfLines={1}>
                  👥 Shared ({effectiveShares.length})
                </ThemedText>
              </TouchableOpacity>
            )}
          </View>

          {kebabActions.length > 0 && (
            <View style={styles.detailsTopRightKebab}>
              <ThemedKebabMenu
                actions={kebabActions}
                compact={true}
                accessibilityLabel={`Options for ${mediaTitle}`}
              />
            </View>
          )}
        </TouchableOpacity>

        <ShareSelector
          visible={isExpanded}
          onClose={handleSharePress}
          itemTitle={mediaTitle}
          itemType="media"
          shares={effectiveShares}
          contacts={availableContacts || []}
          groups={availableGroups || []}
          isLoading={isLoadingShares}
          canShare={canShare}
          canUnshare={canShare}
          onShare={onShareTarget || (() => {})}
          onUnshare={onUnshare || (() => {})}
          testID={`media-share-picker-details-row-${media.id || "card"}`}
        />
        {renderExtractModal()}
      </View>
    );
  }

  if (viewMode === "compact") {
    const effectiveThumbnailUrl = primaryUrl || fallbackUrl || media.url || media.thumbnailUrl || media.downloadUrl;

    if (Platform.OS === "android") {
      if (!effectiveThumbnailUrl) {
        console.warn(`[Media:Compact:MissingUrl] Media ID: ${media.id}, Title: "${mediaTitle}" has no valid image URL:`, {
          id: media.id,
          contentType: media.contentType,
          thumbnailUrl: media.thumbnailUrl,
          downloadUrl: media.downloadUrl,
          url: media.url,
          storageProvider: media.storageProvider,
          isUnavailable: media.isUnavailable,
        });
      } else if (effectiveThumbnailUrl.startsWith("http://")) {
        console.warn(`[Media:Compact:CleartextBlocked] Media ID: ${media.id} uses cleartext HTTP (blocked by default Android network security config): ${effectiveThumbnailUrl}`);
      }
    }

    return (
      <View style={{ position: "relative", width: "auto" }}>
        <ThemedProgressBar loading={isExtracting} />
        <ThemedCompactCard
          entityType="media"
          id={media.id}
          title={mediaTitle}
          thumbnailUrl={effectiveThumbnailUrl}
          isOwner={isOwner}
          isFiveStar={rating === 5}
          isShared={!isOwner}
          selectionMode={selectionMode}
          isSelected={isSelected}
          onSelect={onPress}
          onPress={onPress}
          onLongPress={onLongPress}
          onContextMenu={onContextMenu}
          kebabActions={kebabActions}
          testID={`media-compact-card-${media.id || "card"}`}
        />

        <ShareSelector
          visible={isExpanded}
          onClose={handleSharePress}
          itemTitle={mediaTitle}
          itemType="media"
          shares={effectiveShares}
          contacts={availableContacts || []}
          groups={availableGroups || []}
          isLoading={isLoadingShares}
          canShare={canShare}
          canUnshare={canShare}
          onShare={onShareTarget || (() => {})}
          onUnshare={onUnshare || (() => {})}
          testID={`media-share-picker-compact-${media.id || "card"}`}
        />
        {renderExtractModal()}
      </View>
    );
  }

  if (viewMode === "small") {
    return (
      <View
        style={[
          styles.smallCardContainer,
          { backgroundColor: cardBgColor, borderColor: activeBorderColor },
        ]}
        {...hoverHandlers}
      >
        <ThemedProgressBar loading={isExtracting} />
        <TouchableOpacity
          style={styles.smallCardMain}
          onPress={onPress}
          onLongPress={onLongPress}
          delayLongPress={350}
          activeOpacity={0.7}
          {...contextMenuHandler}
        >
          <View style={styles.smallThumbContainer}>
            <MediaPreview
              url={primaryUrl}
              fallbackUrl={fallbackUrl}
              contentType={media.contentType}
              name={mediaTitle}
              style={styles.smallThumbImage}
              resizeMode="cover"
              isUnavailable={media.isUnavailable}
            />
            {isSelected && (
              <View style={[styles.smallSelectionBadge, { backgroundColor: tintColor }]}>
                <Ionicons name="checkmark" size={10} color="#FFFFFF" />
              </View>
            )}
          </View>

          <View style={styles.smallInfoContainer}>
            <View style={styles.detailsNameRow}>
              <ThemedText type="defaultSemiBold" style={styles.smallFileName} numberOfLines={1}>
                {mediaTitle}
              </ThemedText>
              {!isOwner && (
                <View style={[styles.inlineRoleBadge, { backgroundColor: badgeBg }]}>
                  <ThemedText style={styles.gridRoleBadgeText}>{userRole}</ThemedText>
                </View>
              )}
            </View>

            {media.isUnavailable ? (
              <View style={styles.unavailableWarningBadge} testID="media-unavailable-badge">
                <Ionicons name="alert-circle" size={10} color="#DC3545" />
                <ThemedText style={styles.unavailableWarningText}>External Media Unavailable</ThemedText>
              </View>
            ) : null}
            <ThemedText style={styles.smallMetaText} numberOfLines={1}>
              {media.metadata?.capturedAt
                ? `Captured: ${formatUploadedDateTime(media.metadata.capturedAt)}`
                : `Uploaded: ${formatUploadedDateTime(media.createdAt)}`}
            </ThemedText>
            <View style={styles.smallStatsRow}>
              <View style={styles.smallRatingWrapper} onStartShouldSetResponder={() => true}>
                <ThemedRating
                  value={rating}
                  onChange={handleRatingChange}
                  size={11}
                  showLabel={false}
                  readOnly={!effectiveCanEdit}
                />
              </View>
              {effectiveDate ? (
                <ThemedText style={[styles.smallStatChip, { color: tintColor, fontWeight: "600" }]}>
                  • 📅 {DateParser.parse(effectiveDate).formatted}
                </ThemedText>
              ) : null}
              <ThemedText style={styles.smallStatChip}>• {formatFileSize(media.fileSizeBytes)}</ThemedText>
              <ThemedText style={styles.smallStatChip}>• {formatContentType(media.contentType)}</ThemedText>
              {dimensions ? (
                <ThemedText style={styles.smallStatChip}>• {dimensions}</ThemedText>
              ) : null}
              {memoryCount > 0 ? (
                <ThemedText style={[styles.smallStatChip, { color: tintColor, fontWeight: "600" }]}>
                  • 📖 {memoryCount}
                </ThemedText>
              ) : null}
              {formatLocation(media.location) ? (
                <ThemedText style={styles.smallStatChip} numberOfLines={1}>
                  • 📍 {formatLocation(media.location)}
                </ThemedText>
              ) : null}
            </View>
            <TagListStrip
              entityId={media.id!}
              entityType="media"
              tags={media.tags}
              canEdit={effectiveCanEditTags}
              isOwner={effectiveIsOwnerForTags}
              currentUserId={currentUserId}
              compact={true}
            />

            {effectiveShares.length > 0 && (
              <TouchableOpacity
                style={[styles.compactSharePill, { backgroundColor: badgeBg, marginTop: 4 }]}
                onPress={handleSharePress}
                activeOpacity={0.7}
                testID={`media-share-pill-small-${media.id || "card"}`}
              >
                <Ionicons name="people-outline" size={11} color={tintColor} />
                <ThemedText style={[styles.compactShareText, { color: tintColor, fontSize: 10 }]}>
                  Shared ({effectiveShares.length})
                </ThemedText>
              </TouchableOpacity>
            )}
          </View>

          {kebabActions.length > 0 && (
            <View style={styles.detailsTopRightKebab}>
              <ThemedKebabMenu
                actions={kebabActions}
                compact={true}
                accessibilityLabel={`Options for ${mediaTitle}`}
              />
            </View>
          )}
        </TouchableOpacity>

        <ShareSelector
          visible={isExpanded}
          onClose={handleSharePress}
          itemTitle={mediaTitle}
          itemType="media"
          shares={effectiveShares}
          contacts={availableContacts || []}
          groups={availableGroups || []}
          isLoading={isLoadingShares}
          canShare={canShare}
          canUnshare={canShare}
          onShare={onShareTarget || (() => {})}
          onUnshare={onUnshare || (() => {})}
          testID={`media-share-picker-small-${media.id || "card"}`}
        />

        {activeHovered && (
          <MediaHoverMetadataFooter
            media={media}
            borderColor={borderColor}
            badgeBg={badgeBg}
            tintColor={tintColor}
          />
        )}
        {renderExtractModal()}
      </View>
    );
  }

  return (
    <TouchableOpacity
      style={[
        styles.imageContainer,
        compact && styles.imageContainerCompact,
        viewMode === "large" && styles.imageContainerLarge,
        { backgroundColor: cardBgColor, borderColor: activeBorderColor },
      ]}
      onPress={onPress}
      onLongPress={onLongPress}
      delayLongPress={350}
      activeOpacity={0.7}
      {...hoverHandlers}
      {...contextMenuHandler}
    >
      <ThemedProgressBar loading={isExtracting} />
      <MediaPreview
        url={primaryUrl}
        fallbackUrl={fallbackUrl}
        contentType={media.contentType}
        name={mediaTitle}
        style={compact ? styles.imageCompact : viewMode === "large" ? styles.imageLarge : styles.imageMedium}
        resizeMode={viewMode === "large" ? "contain" : "cover"}
        isUnavailable={media.isUnavailable}
      />

      {/* Top right platform symbols and kebab menu */}
      <View style={styles.mediaCardTopRightKebab}>
        <View style={styles.mediaSymbolOverlayRow}>
          {isOwner ? (
            <ThemedPlatformSymbol type="owner" size="small" />
          ) : (
            <ThemedPlatformSymbol type="shared" size="small" />
          )}
          {rating === 5 && (
            <ThemedPlatformSymbol type="fivestar" size="small" />
          )}
        </View>
        {kebabActions.length > 0 && (
          <ThemedKebabMenu
            actions={kebabActions}
            compact={compact}
            accessibilityLabel={`Options for ${mediaTitle}`}
            triggerStyle={styles.mediaKebabTrigger}
          />
        )}
      </View>

      <View style={[styles.cardContent, compact && styles.cardContentCompact]}>
        <View style={styles.gridTitleRow}>
          <ThemedText type="defaultSemiBold" style={styles.cardTitle} numberOfLines={1}>
            {mediaTitle}
          </ThemedText>
        </View>

        {effectiveDate ? (
          <View style={styles.cardDateRow} testID={`media-date-${media.id || "card"}`}>
            <Ionicons name={isDateUserDefined(effectiveDate) ? "calendar" : "calendar-outline"} size={11} color={tintColor} style={{ marginRight: 3 }} />
            <ThemedText style={[styles.cardDateText, { color: tintColor }]} numberOfLines={1}>
              {DateParser.parse(effectiveDate).formatted} {isDateUserDefined(effectiveDate) ? "(Manual)" : ""}
            </ThemedText>
          </View>
        ) : null}

        {media.isUnavailable ? (
          <View style={styles.unavailableWarningBadge} testID="media-unavailable-badge">
            <Ionicons name="alert-circle" size={11} color="#DC3545" />
            <ThemedText style={styles.unavailableWarningText}>External Media Unavailable</ThemedText>
          </View>
        ) : null}

        {formatLocation(media.location) ? (
          <ThemedText style={styles.cardLocation} numberOfLines={1}>
            📍 {formatLocation(media.location)}
          </ThemedText>
        ) : null}

        {media.description ? (
          <ThemedText style={styles.cardDesc} numberOfLines={2}>
            {media.description}
          </ThemedText>
        ) : null}

        <View style={styles.cardRatingRow} onStartShouldSetResponder={() => true}>
          <ThemedRating
            value={rating}
            onChange={handleRatingChange}
            size={viewMode === "large" ? 18 : 14}
            showLabel={false}
            readOnly={!effectiveCanEdit}
            style={styles.cardRatingContainer}
          />
        </View>

        <View style={styles.cardStatsBar}>
          <View style={[styles.statChip, { backgroundColor: badgeBg }]}>
            <Ionicons name="save-outline" size={9} color={tintColor} />
            <ThemedText style={styles.statChipText}>{formatFileSize(media.fileSizeBytes)}</ThemedText>
          </View>
          <View style={[styles.statChip, { backgroundColor: badgeBg }]}>
            <ThemedText style={[styles.statChipText, { color: tintColor, fontWeight: "700" }]}>
              {formatContentType(media.contentType)}
            </ThemedText>
          </View>
          {dimensions ? (
            <View style={[styles.statChip, { backgroundColor: badgeBg }]}>
              <Ionicons name="scan-outline" size={9} color={tintColor} />
              <ThemedText style={styles.statChipText}>{dimensions}</ThemedText>
            </View>
          ) : null}
          {media.fileHash ? (
            <View style={[styles.statChip, { backgroundColor: badgeBg }]}>
              <Ionicons name="shield-checkmark-outline" size={9} color={tintColor} />
              <ThemedText style={styles.statChipText}>{formatShortHash(media.fileHash)}</ThemedText>
            </View>
          ) : null}
        </View>

        {viewMode === "large" ? (
          <MediaStatsSection
            media={media}
            badgeBg={badgeBg}
            borderColor={borderColor}
            tintColor={tintColor}
          />
        ) : null}

        {memoryCount > 0 ? (
          <View style={[styles.memoryInfo, { backgroundColor: badgeBg }]}>
            <ThemedText style={styles.memoriesLabel}>Memories</ThemedText>
            <ThemedText style={styles.memoryInfoText} numberOfLines={1}>
              📖 {memoryCount} {memoryCount === 1 ? "memory" : "memories"}
            </ThemedText>
          </View>
        ) : null}

        {/* Contact & Groups Section (Single Section Standard) */}
        {(() => {
          const linkedTargets = [
            ...resolvedContacts.map((c) => ({
              id: c.id,
              type: "contact" as const,
              displayName: c.displayName,
              photoIconUrl: c.photoIconUrl,
              isOwner: c.isOwner,
            })),
            ...resolvedGroups.map((g) => ({
              id: g.id,
              type: "group" as const,
              displayName: g.name,
              photoIconUrl: undefined,
              isOwner: false,
            })),
          ];
          if (linkedTargets.length === 0) return null;

          return (
            <View style={styles.contactAndGroupsSection}>
              <View style={styles.contactAndGroupsHeaderRow}>
                <Ionicons name="people-outline" size={11} color={tintColor} />
                <ThemedText type="stamp" style={[styles.contactAndGroupsStamp, { color: textMuted }]}>
                  CONTACT &amp; GROUPS ({linkedTargets.length})
                </ThemedText>
              </View>
              <View style={styles.chipRow}>
                {linkedTargets.map((s, idx) => (
                  <ThemedChip
                    key={`media-target-${s.id || idx}`}
                    entityType={s.type === "group" ? "group" : "contact"}
                    id={s.id}
                    label={s.displayName || (s.type === "group" ? "Group" : "Contact")}
                    thumbnailUrl={s.photoIconUrl}
                    compact={compact}
                  />
                ))}
              </View>
            </View>
          );
        })()}

        {effectiveShares.length > 0 && (
          <View style={styles.sharePillContainer}>
            <TouchableOpacity
              style={[styles.compactSharePill, { backgroundColor: badgeBg }]}
              onPress={handleSharePress}
              activeOpacity={0.7}
              testID="media-share-pill-normal"
            >
              <Ionicons name="people-outline" size={12} color={tintColor} />
              <ThemedText style={[styles.compactShareText, { color: tintColor }]}>
                Shared ({effectiveShares.length})
              </ThemedText>
            </TouchableOpacity>
          </View>
        )}

        <TagListStrip
          entityId={media.id!}
          entityType="media"
          tags={media.tags}
          canEdit={effectiveCanEditTags}
          isOwner={effectiveIsOwnerForTags}
          currentUserId={currentUserId}
        />

        <View style={[styles.uploadedRow, { borderTopColor: borderColor }]}>
          <Ionicons name="time-outline" size={11} color={tintColor} style={{ opacity: 0.7 }} />
          <ThemedText style={styles.uploadedLabel}>Uploaded: </ThemedText>
          <ThemedText style={styles.uploadedValue} numberOfLines={1}>
            {formatUploadedDateTime(media.createdAt)}
          </ThemedText>
        </View>

        {resolvedShowOwner && (
          <View style={styles.ownerRow}>
            <ThemedText style={styles.ownerLabel}>{isOwner ? "Owned by: " : "Shared by: "}</ThemedText>
            <ThemedText style={media.ownerDisplayName ? styles.ownerValueName : styles.ownerValue} numberOfLines={1}>
              {media.ownerDisplayName || media.ownerId}
            </ThemedText>
          </View>
        )}
      </View>

      {/* Render hover metadata at the bottom of the media card when hovering */}
      {activeHovered && (
        <MediaHoverMetadataFooter
          media={media}
          borderColor={borderColor}
          badgeBg={badgeBg}
          tintColor={tintColor}
        />
      )}

      {!isOwner && (
        <View style={[styles.gridRoleBadge, { backgroundColor: badgeBg }]}>
          <ThemedText style={styles.gridRoleBadgeText}>{userRole}</ThemedText>
        </View>
      )}

      {isSelected && (
        <View
          style={[
            styles.selectionBadge,
            {
              backgroundColor: tintColor,
              right: kebabActions.length > 0 ? 34 : 6,
            },
          ]}
        >
          <ThemedText style={styles.selectionText}>✓</ThemedText>
        </View>
      )}

      <ShareSelector
        visible={isExpanded}
        onClose={handleSharePress}
        itemTitle={mediaTitle}
        itemType="media"
        shares={effectiveShares}
        contacts={availableContacts || []}
        groups={availableGroups || []}
        isLoading={isLoadingShares}
        canShare={canShare}
        canUnshare={canShare}
        onShare={onShareTarget || (() => {})}
        onUnshare={onUnshare || (() => {})}
        testID={`media-share-picker-grid-${media.id || "card"}`}
      />
      {renderExtractModal()}
    </TouchableOpacity>
  );
}

const styles = StyleSheet.create({
  // Grid styles
  imageContainer: {
    position: "relative",
    width: "100%",
    alignSelf: "stretch",
    marginHorizontal: 0,
    marginVertical: 2,
    borderRadius: 0,
    overflow: "hidden",
    backgroundColor: "rgba(150, 150, 150, 0.05)",
    borderWidth: 1,
    borderColor: "rgba(150, 150, 150, 0.15)",
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.08,
    shadowRadius: 3,
    elevation: 2,
  },
  imageContainerCompact: {
    marginHorizontal: 0,
    marginVertical: 1,
    borderRadius: 0,
  },
  imageContainerLarge: {
    width: "100%",
    marginHorizontal: 0,
    marginVertical: 4,
  },
  imageSelected: {
    borderColor: "#4A90E2",
  },
  image: {
    width: "100%",
    height: 90,
  },
  imageCompact: {
    width: "100%",
    height: 70,
  },
  cardContent: {
    padding: 4,
  },
  cardContentCompact: {
    padding: 3,
  },
  gridTitleRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 2,
  },
  cardTitle: {
    fontSize: 11.5,
    fontWeight: "600",
    flex: 1,
  },
  cardDateRow: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 2,
  },
  cardDateText: {
    fontSize: 9.5,
    fontWeight: "600",
  },
  detailDateRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
    marginBottom: 2,
  },
  gridActionRow: {
    flexDirection: "row",
    gap: 2,
  },
  gridActionBtn: {
    padding: 2,
    backgroundColor: "rgba(0,0,0,0.05)",
    borderRadius: 0,
  },
  gridDeleteBtn: {
    backgroundColor: "rgba(217, 48, 37, 0.1)",
  },
  gridActionIcon: {
    fontSize: 9,
    opacity: 0.6,
  },
  cardLocation: {
    fontSize: 9.5,
    opacity: 0.6,
    marginBottom: 2,
  },
  cardDesc: {
    fontSize: 9.5,
    opacity: 0.7,
    marginBottom: 2,
  },
  memoryInfo: {
    flexDirection: "row",
    alignItems: "center",
    gap: 3,
    paddingHorizontal: 4,
    paddingVertical: 2,
    borderRadius: 0,
    alignSelf: "flex-start",
    marginTop: 2,
    marginBottom: 2,
  },
  memoriesLabel: {
    fontSize: 8,
    fontWeight: "bold",
    opacity: 0.4,
    textTransform: "uppercase",
  },
  memoryInfoText: {
    fontSize: 9.5,
    opacity: 0.7,
  },
  gridRoleBadge: {
    position: "absolute",
    top: 4,
    left: 4,
    paddingHorizontal: 4,
    paddingVertical: 1,
    borderRadius: 0,
  },
  gridRoleBadgeText: {
    fontSize: 7.5,
    fontWeight: "bold",
    textTransform: "uppercase",
    opacity: 0.6,
  },
  selectionBadge: {
    position: "absolute",
    top: 6,
    right: 34,
    backgroundColor: "#1E293B",
    width: 16,
    height: 16,
    borderRadius: 0,
    justifyContent: "center",
    alignItems: "center",
    zIndex: 14,
  },
  selectionText: {
    color: "white",
    fontSize: 10,
    fontWeight: "bold",
  },
  mediaCardTopRightKebab: {
    position: "absolute",
    top: 6,
    right: 6,
    zIndex: 15,
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
  },
  mediaSymbolOverlayRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 3,
  },
  compactTopRightKebab: {
    position: "absolute",
    top: 2,
    right: 2,
    zIndex: 15,
  },
  mediaKebabTrigger: {
    backgroundColor: "rgba(255, 255, 255, 0.88)",
    borderColor: "rgba(0, 0, 0, 0.12)",
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.15,
    shadowRadius: 2,
    elevation: 3,
  },
  detailPreviewWrapper: {
    position: "relative",
    width: "100%",
  },
  detailTopRightKebab: {
    position: "absolute",
    top: 10,
    right: 10,
    zIndex: 15,
  },
  detailsTopRightKebab: {
    alignSelf: "flex-start",
    marginTop: 2,
  },

  // Detail styles
  detailContainer: {
    width: "100%",
    borderRadius: 12,
    overflow: "hidden",
  },
  fullImage: {
    width: "100%",
    height: 300,
    backgroundColor: "#000",
  },
  infoSection: {
    padding: 16,
    gap: 12,
  },
  titleRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: 16,
  },
  fileName: {
    flex: 1,
  },
  detailActions: {
    flexDirection: "row",
    gap: 8,
  },
  actionIconBtn: {
    padding: 8,
    borderRadius: 8,
    backgroundColor: "rgba(0,0,0,0.05)",
  },
  deleteIconBtn: {
    backgroundColor: "rgba(217, 48, 37, 0.1)",
  },
  actionIcon: {
    fontSize: 14,
  },
  locationText: {
    fontSize: 14,
    opacity: 0.7,
  },
  descriptionText: {
    fontSize: 15,
    lineHeight: 22,
    opacity: 0.9,
  },
  metaText: {
    fontSize: 12,
    opacity: 0.5,
  },
  badgeRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    marginTop: 12,
  },
  roleBadge: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 6,
  },
  roleBadgeText: {
    color: "#4A90E2",
    fontSize: 10,
    fontWeight: "bold",
    textTransform: "uppercase",
  },
  contactAndGroupsSection: {
    marginTop: 4,
    paddingTop: 4,
    borderTopWidth: 1,
    borderTopColor: "rgba(0,0,0,0.05)",
  },
  contactAndGroupsHeaderRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
    marginBottom: 4,
  },
  contactAndGroupsStamp: {
    fontSize: 9,
    letterSpacing: 0.5,
  },
  chipRow: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 4,
  },
  sharesContainer: {
    marginTop: 4,
    marginBottom: 8,
  },
  sharesHeaderRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 6,
  },
  manageSharesText: {
    fontSize: 11,
    fontWeight: "600",
  },
  inlineSharingSection: {
    marginTop: 12,
    marginBottom: 12,
    paddingTop: 12,
    borderTopWidth: 1,
    borderTopColor: "rgba(0,0,0,0.05)",
  },
  inlineSharingHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 8,
  },
  inlineSharingTitle: {
    fontSize: 13,
    letterSpacing: -0.2,
  },
  collapseBtn: {
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 6,
  },
  collapseBtnText: {
    fontSize: 11,
    fontWeight: "600",
    opacity: 0.7,
  },
  sharesLabel: {
    fontSize: 10,
    fontWeight: "bold",
    opacity: 0.4,
    textTransform: "uppercase",
  },
  sharesList: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 4,
  },
  uploadedRow: {
    marginTop: 3,
    flexDirection: "row",
    alignItems: "center",
    borderTopWidth: 1,
    borderTopColor: "rgba(0,0,0,0.05)",
    paddingTop: 2,
    gap: 3,
  },
  uploadedLabel: {
    fontSize: 8,
    fontWeight: "bold",
    opacity: 0.35,
    textTransform: "uppercase",
  },
  uploadedValue: {
    fontSize: 8.5,
    opacity: 0.65,
    fontWeight: "500",
    flex: 1,
  },
  detailUploadedRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
  },
  ownerRow: {
    marginTop: 2,
    flexDirection: "row",
    alignItems: "center",
    gap: 3,
  },
  ownerLabel: {
    fontSize: 8,
    fontWeight: "bold",
    opacity: 0.3,
    textTransform: "uppercase",
  },
  ownerValue: {
    fontSize: 8.5,
    opacity: 0.5,
    fontFamily: "monospace",
    flex: 1,
  },
  ownerValueName: {
    fontSize: 9,
    opacity: 0.7,
    fontWeight: "bold",
    flex: 1,
  },
  mediaPlaceholder: {
    justifyContent: "center",
    alignItems: "center",
    gap: 4,
  },
  mediaPlaceholderText: {
    fontSize: 10,
    fontWeight: "bold",
    opacity: 0.6,
  },
  videoPreviewContainer: {
    justifyContent: "center",
    alignItems: "center",
    position: "relative",
    overflow: "hidden",
  },
  videoPlayBadge: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: "rgba(0, 0, 0, 0.6)",
    alignItems: "center",
    justifyContent: "center",
    borderWidth: 1.5,
    borderColor: "rgba(255, 255, 255, 0.85)",
  },
  ownerText: {
    fontSize: 9.5,
    opacity: 0.6,
    fontStyle: "italic",
  },
  mediaPlayerContainer: {
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: "rgba(74, 144, 226, 0.05)",
    padding: 10,
    borderRadius: 0,
  },
  imageMedium: {
    width: "100%",
    height: 160,
  },
  imageLarge: {
    width: "100%",
    height: 500,
    backgroundColor: "rgba(0,0,0,0.03)",
  },
  // Details view styles
  detailsRowContainer: {
    position: "relative",
    width: "100%",
    alignSelf: "stretch",
    borderRadius: 0,
    borderWidth: 1,
    overflow: "hidden",
    marginVertical: 1,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.08,
    shadowRadius: 3,
    elevation: 2,
  },
  detailsRowMain: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: ButtonPaddings.action.paddingVertical,
    paddingHorizontal: ButtonPaddings.action.paddingHorizontal,
    gap: 6,
  },
  detailsCheckbox: {
    width: 14,
    height: 14,
    borderRadius: 0,
    borderWidth: 1,
    borderColor: "rgba(150, 150, 150, 0.5)",
    alignItems: "center",
    justifyContent: "center",
  },
  detailsThumb: {
    width: 28,
    height: 28,
    borderRadius: 0,
    overflow: "hidden",
  },
  detailsImage: {
    width: 28,
    height: 28,
  },
  detailsInfo: {
    flex: 1,
    justifyContent: "center",
    gap: 1,
  },
  detailsNameRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
  },
  detailsFileName: {
    fontSize: 11,
    fontWeight: "600",
    flexShrink: 1,
  },
  detailsSubRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
    flexWrap: "wrap",
  },
  detailsMetaText: {
    fontSize: 9.5,
    opacity: 0.65,
  },
  inlineRoleBadge: {
    paddingHorizontal: 3,
    paddingVertical: 1,
    borderRadius: 0,
  },
  // Small view styles
  smallCardContainer: {
    position: "relative",
    width: "100%",
    alignSelf: "stretch",
    borderRadius: 0,
    borderWidth: 1,
    overflow: "hidden",
    marginVertical: 1,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.08,
    shadowRadius: 3,
    elevation: 2,
  },
  smallCardMain: {
    flexDirection: "row",
    alignItems: "center",
    padding: 3,
    gap: 6,
  },
  smallThumbContainer: {
    width: 34,
    height: 34,
    borderRadius: 0,
    overflow: "hidden",
    position: "relative",
  },
  smallThumbImage: {
    width: 34,
    height: 34,
  },
  smallSelectionBadge: {
    position: "absolute",
    top: 2,
    right: 2,
    width: 14,
    height: 14,
    borderRadius: 0,
    alignItems: "center",
    justifyContent: "center",
  },
  smallInfoContainer: {
    flex: 1,
    justifyContent: "center",
    gap: 1,
  },
  smallFileName: {
    fontSize: 11,
    fontWeight: "600",
  },
  smallMetaText: {
    fontSize: 9,
    opacity: 0.6,
  },
  // Compact view styles (14 columns)
  compactMediaContainer: {
    width: "100%",
    aspectRatio: 1,
    borderRadius: 0,
    borderWidth: 1,
    overflow: "hidden",
    justifyContent: "center",
    alignItems: "center",
    position: "relative",
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.06,
    shadowRadius: 2,
    elevation: 1,
  },
  compactMediaImage: {
    width: "100%",
    height: "100%",
    aspectRatio: 1,
  },
  // Stats bar and badges
  cardStatsBar: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 3,
    marginTop: 2,
    marginBottom: 2,
  },
  statChip: {
    flexDirection: "row",
    alignItems: "center",
    gap: 2,
    paddingHorizontal: 4,
    paddingVertical: 1,
    borderRadius: 0,
  },
  statChipText: {
    fontSize: 8.5,
    fontWeight: "500",
    opacity: 0.8,
  },
  inlineMemoryBadge: {
    paddingHorizontal: 3,
    paddingVertical: 1,
    borderRadius: 0,
  },
  inlineMemoryBadgeText: {
    fontSize: 8.5,
    fontWeight: "bold",
  },
  smallStatsRow: {
    flexDirection: "row",
    alignItems: "center",
    flexWrap: "wrap",
    gap: 3,
    marginTop: 1,
  },
  smallStatChip: {
    fontSize: 8.5,
    opacity: 0.65,
  },
  cardRatingRow: {
    marginTop: 2,
    marginBottom: 2,
    flexDirection: "row",
    alignItems: "center",
  },
  cardRatingContainer: {
    paddingVertical: 1,
  },
  detailRatingRow: {
    marginVertical: 4,
  },
  detailsRatingWrapper: {
    flexDirection: "row",
    alignItems: "center",
  },
  smallRatingWrapper: {
    flexDirection: "row",
    alignItems: "center",
  },
  compactRatingBadge: {
    position: "absolute",
    bottom: 2,
    left: 2,
    flexDirection: "row",
    alignItems: "center",
    gap: 2,
    paddingHorizontal: 3,
    paddingVertical: 1,
    borderRadius: 2,
    backgroundColor: "rgba(0, 0, 0, 0.65)",
  },
  compactRatingText: {
    fontSize: 8.5,
    fontWeight: "700",
    color: "#FFFFFF",
  },
  unavailableWarningBadge: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
    backgroundColor: "rgba(220, 53, 69, 0.12)",
    borderColor: "rgba(220, 53, 69, 0.4)",
    borderWidth: 1,
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 2,
    marginVertical: 3,
    alignSelf: "flex-start",
  },
  unavailableWarningText: {
    fontSize: 9.5,
    fontWeight: "700",
    color: "#DC3545",
  },
  compactUnavailableBadge: {
    position: "absolute",
    top: 2,
    left: 2,
    backgroundColor: "rgba(220, 53, 69, 0.9)",
    borderRadius: 2,
    paddingHorizontal: 3,
    paddingVertical: 1,
    zIndex: 10,
  },
  sharePillContainer: {
    marginTop: 6,
    flexDirection: "row",
    alignItems: "center",
  },
  compactSharePill: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: ButtonPaddings.compact.paddingHorizontal,
    paddingVertical: ButtonPaddings.compact.paddingVertical,
    borderRadius: 12,
    gap: 4,
    alignSelf: "flex-start",
  },
  compactShareText: {
    fontSize: 11,
    fontWeight: "600",
  },
});
